import {
  type ReactElement,
  useEffect,
  useState,
  useContext,
  createContext,
} from 'react';

import { type State, type City, getStates } from '../../services/StatesService';
import Search from './Search';
import { useDentistStoreState } from '../../stores/usePaginação';
import { useTotalClinicDataStore } from '../../stores/usePaginacao';

export interface Specialty {
  idEspecialidade: number;
  nomeEspecialidade: string;
  quantidade: number;
}

interface Plan {
  id: number;
  name: string;
}

export interface Dentist {
  endereco: string;
  bairro: string;
  cep: string;
  cidade: string;
  complemento?: string;
  especialidade: string;
  idEspecialidade: string;
  idFuncionario: number | string;
  idPlano: null;
  latitude: string;
  logradouro: string;
  longitude: string;
  nomeFuncionario: string;
  numero?: number | string;
  telefone: { telefone: string; tipo: string }[];
  tipoDeLogradouro: 'Rua';
  ufSigla: 'PERNAMBUCO';
  nomeFantasia?: string;
  tipoEstabelecimento?: string;
  qualiss?: string;
  produtos?: string;
}

interface DentistsContext {
  states: State[];
  specialties: Specialty[];
  specialty: Specialty;
  city: City;
  state: State;
  plan: Plan;
  plans: Plan[];
  loading: boolean;
  found: boolean;
  docList: Dentist[];
  canSearch: boolean;
  handleSearch: () => void;
  setSpecialty: React.Dispatch<React.SetStateAction<Specialty>>;
  setState: React.Dispatch<React.SetStateAction<State>>;
  setCity: React.Dispatch<React.SetStateAction<City>>;
  setPlan: React.Dispatch<React.SetStateAction<Plan>>;
  reset: () => void;
  mapCenter: { lat: number; lng: number };
  setMapCenter: React.Dispatch<
    React.SetStateAction<{ lat: number; lng: number }>
  >;
  selectedDentist: Dentist | null;
  setSelectedDentist: React.Dispatch<React.SetStateAction<Dentist | null>>;
}

const DentistsContext = createContext({} as DentistsContext);

export const useDentistsContext = (): DentistsContext =>
  useContext(DentistsContext);

export default function DentistSearch(): ReactElement {
  const [states, setStates] = useState<State[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [specialty, setSpecialty] = useState<Specialty>(null);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<State>(null);
  const [city, setCity] = useState<City>(null);
  const [plan, setPlan] = useState<Plan>(null);
  const [found, setFound] = useState(false);
  const docList = useDentistStoreState(x => x.data);
  const setDocList = useDentistStoreState(x => x.setDocList);
  const setTotal = useTotalClinicDataStore(x => x.setTotalClinicData);
  const [dentista, setDentista] = useState<any>([]);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(
    null,
  );
  const [selectedDentist, setSelectedDentist] = useState<Dentist | null>(null);

  useEffect(() => {
    getStates().then(states => setStates(states));

    const getRequest = async url => {
      const resp = await fetch(url).then(resp => resp.json());
      return resp.dataObject.dados;
    };

    getRequest(
      `${process.env.REACT_APP_GET_DENTISTS_SPECIALTY}?token=${process.env.REACT_APP_TOKEN_S4E}&sigla=df`,
    ).then(s => setSpecialties(s.filter(s => s.idEspecialidade !== 47)));
    setPlans([
      { id: 1, name: 'OdontoClínico' },
      { id: 2, name: 'OdontoDoc' },
      { id: 3, name: 'OdontoOrto' },
      { id: 4, name: 'OdontoPrótese' },
      { id: 5, name: 'OdontoKids' },
    ]);
  }, []);

  useEffect(() => {
    const postRequest = async (url, data) => {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }).then(resp => resp.json());
      return resp.dados;
    };
    if (specialty && state && city) {
      const data = {
        uf: state.Id,
        municipio: city.Id,
        especialidade: specialty.idEspecialidade,
      };
    }
  }, [specialty, state, city, plan]);

  function handleSearch() {
    setLoading(true);
    /* fetch(
      `${process.env.REACT_APP_GET_ACTIVE_DENTISTS}?token=${
        process.env.REACT_APP_TOKEN_S4E
      }&idEstado=${state.Id}&idCidade=${city.Id}&pagina=${1}`,
    ) */
    fetch(
      `${process.env.REACT_APP_OG_API_URL}/info/RedeVendas?&ufId=${state.Id}&municipioId=${city.Id}&especialidadeId=${specialty.idEspecialidade}&quantidade=5`,
    )
      .then(resp => resp.json())
      .then(data => {
        console.log('data: ', data);
        const c = data.clinicas
          .filter(d => d.longitude)
          .map(d => ({
            ...d,
            longitude: d.longitude.replace(',', '.'),
            latitude: d.latitude.replace(',', '.'),
          }));
        Promise.all(c)
          .then(updatedDentists => {
            setDocList(c);
            setTotal(data.total || 0);
          })
          .catch(err => {
            console.log(err);
            setDocList(c);
            setTotal(data.total || 0);
          });
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
        setFound(true);
      });
  }

  const reset = () => {
    setFound(false);
    setDocList([]);
    setCity(null);
    setState(null);
    setSpecialty(null);
    setPlan(null);
    setLoading(false);
  };

  const canSearch = !!specialty && !!state && !!city && !loading;
  return (
    <DentistsContext.Provider
      value={{
        states,
        docList,
        plan,
        specialties,
        found,
        plans,
        specialty,
        loading,
        state,
        city,
        canSearch,
        setSpecialty,
        handleSearch,
        setState,
        setPlan,
        setCity,
        reset,
        mapCenter,
        setMapCenter,
        selectedDentist,
        setSelectedDentist,
      }}
    >
      <Search />
    </DentistsContext.Provider>
  );
}
